/**
 * Tiny typed fetch wrapper. The dev-token (if set) is appended as a Bearer
 * header so local devs hitting `/api/v1/providers` from a non-cookie session
 * still authenticate. Cookie sessions just ride on `credentials: 'include'`.
 *
 * No auto-retry, no global state, no toast plumbing — pages own their loading
 * + error UX via TanStack Query's primitives. This module is just the
 * fetch-and-parse round-trip.
 */
const DEV_TOKEN_STORAGE_KEY = 'kennisbank.devToken';

function devTokenHeader(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const token = window.sessionStorage.getItem(DEV_TOKEN_STORAGE_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON */
  }
  if (!res.ok) {
    const err = body as { error?: string; message?: string; detail?: unknown } | null;
    throw new ApiError(
      res.status,
      err?.error ?? `http_${res.status}`,
      err?.message ?? `Request failed (${res.status})`,
      err?.detail,
    );
  }
  return body as T;
}

/**
 * GET die een bestand (blob) teruggeeft i.p.v. JSON — voor downloads zoals het
 * e-learningcertificaat. Foutresponses zijn wél JSON en gaan door `handle`.
 * De bestandsnaam komt uit de Content-Disposition-header (indien aanwezig).
 */
export async function apiGetBlob(path: string): Promise<{ blob: Blob; filename: string | null }> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { ...devTokenHeader() },
  });
  if (!res.ok) return handle<never>(res);
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  return { blob: await res.blob(), filename: match?.[1] ?? null };
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, string | number | string[] | undefined>,
): Promise<T> {
  const pairs: string[] = [];
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === '') continue;
      if (Array.isArray(v)) {
        // Repeated params (`?k=a&k=b`) — Express parses these as string[].
        for (const item of v) {
          if (item === undefined || item === '') continue;
          pairs.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(item))}`);
        }
      } else {
        pairs.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
      }
    }
  }
  const query = pairs.length > 0 ? `?${pairs.join('&')}` : '';
  const res = await fetch(`${path}${query}`, {
    credentials: 'include',
    headers: { ...devTokenHeader() },
  });
  return handle<T>(res);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...devTokenHeader() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handle<T>(res);
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...devTokenHeader() },
    body: JSON.stringify(body),
  });
  return handle<T>(res);
}

export async function apiPatch<T>(
  path: string,
  body: unknown,
  query?: Record<string, string>,
): Promise<T> {
  const qs = query ? '?' + new URLSearchParams(query).toString() : '';
  const res = await fetch(`${path}${qs}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...devTokenHeader() },
    body: JSON.stringify(body),
  });
  return handle<T>(res);
}

export async function apiDelete<T = void>(
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const headers: Record<string, string> = { ...devTokenHeader(), ...(extraHeaders ?? {}) };
  if (body !== undefined) headers['content-type'] = 'application/json';
  const res = await fetch(path, {
    method: 'DELETE',
    credentials: 'include',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  let parsed: unknown = null;
  try {
    parsed = await res.json();
  } catch {
    /* non-JSON */
  }
  if (!res.ok) {
    const err = parsed as { error?: string; message?: string; detail?: unknown } | null;
    throw new ApiError(
      res.status,
      err?.error ?? `http_${res.status}`,
      err?.message ?? `Request failed (${res.status})`,
      err?.detail,
    );
  }
  return parsed as T;
}
